import React, { useCallback, useEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/IntentsPage';
import block from 'propmods';

import { granetApi } from '../../api';
import { GrammarPageContext } from '../../context/grammarPageContext';
import { useSettingsPageContext } from '../../context/settingsPageContext';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { wrapInText } from '../../lib/i18n-utils';
import { routes } from '../../lib/routes';
import { GrammarIntentShort } from '../../model/grammar';
import { getBaseTooltipReducer } from '../../reducers/tooltips';
import { ActionButton } from '../ActionButton/ActionButton';
import { DevPreviewBanner } from '../DocumentationPage/DevPreviewBanner';
import { Header } from '../Form/Form';
import { FormError } from '../Form/FormError';
import { GrammarPageIntent } from '../GrammarPageIntent/GrammarPageIntent';
import Link from '../Link/Link';
import { Loading } from '../Loading/Loading';

import './GrammarPageIntentsSection.scss';

const t = i18n(keyset);
const b = block('GrammarPageIntentsSection');

const GrammarPageIntentsContent: React.FC = () => {
    const skill = useCurrentAliceApp()!;
    const { isDraft, isEditable } = useSettingsPageContext();
    const [intents, setIntents] = useState<GrammarIntentShort[]>([]);

    const fetchIntents = useCallback(
        () =>
            granetApi.getGrammarIntentList({
                skillId: skill.id,
                channel: skill.channel,
                isDraft,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDraft, skill.id],
    );

    const [fetchIntentsHandler, state] = useFetchingHandler({
        handler: () =>
            granetApi.getGrammarIntentList({
                skillId: skill.id,
                channel: skill.channel,
                isDraft,
            }),
        onLoad: setIntents,
    });

    const [createIntent] = useFetchingHandler({
        omitResult: true,
        throwErrors: true,
        handler: () => granetApi.createGrammarIntent({ skillId: skill.id, channel: skill.channel }),
        onLoad: (intent) => setIntents([...intents, intent]),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchIntentsHandler(), [isDraft]);

    if (state === 'loading') {
        return <Loading centered size="l" />;
    }

    if (state === 'error') {
        return <FormError>{t('error')}</FormError>;
    }

    return (
        <GrammarPageContext.Provider
            value={{
                refreshIntents: () => fetchIntents().then(setIntents),
                removeIntentFromView: (id) => setIntents(intents.filter((intent) => intent.id !== id)),
            }}
        >
            <div {...b('forms')}>
                {intents.map((intent) => {
                    return (
                        <div {...b('form')} key={intent.id}>
                            <GrammarPageIntent intent={intent} />
                        </div>
                    );
                })}
            </div>
            {isEditable && (
                <div {...b('button-create')}>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: '' })}
                        action={async () => {
                            await createIntent();
                            return 'clear';
                        }}
                        defaultTooltipState={{ tooltipVisible: false }}
                    >
                        {t('create-intent')}
                    </ActionButton>
                </div>
            )}
        </GrammarPageContext.Provider>
    );
};

const displayDevPreviewWarnings = __appConfig.app.displayDevPreviewWarnings;

export const GrammarPageIntentsSection: React.FC = () => {
    return (
        <div {...b()}>
            <Header
                title={t('intents-title')}
                subtitle={
                    <>
                        {displayDevPreviewWarnings && <DevPreviewBanner />}
                        {wrapInText((s) => (
                            <Link to={routes.documentation.aliceSkills.nlu} external target="_blank">
                                {s}
                            </Link>
                        ))(t('intents-description'))}
                    </>
                }
            />
            <GrammarPageIntentsContent />
        </div>
    );
};
